import type { PlatformAdapter } from '@petrhythm/core';
import { saveProductFile } from './file-delivery.js';

export type ShareFilesResult = 'shared' | 'saved' | 'copied' | 'cancelled' | 'failed';

/**
 * 파일을 공유 시트로 보낸다. 파일 공유가 안 되는 브라우저(카톡 인앱 등)·토스에서는 기기에 저장하고
 * 텍스트는 복사한다. 실패를 성공으로 바꾸지 않는다.
 */
export async function shareFiles(platform: PlatformAdapter, input: { title: string; text?: string; files: File[] }): Promise<ShareFilesResult> {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const canShareFiles = platform.auth.kind !== 'toss' && Boolean(nav?.share) && input.files.length > 0 && (!nav?.canShare || nav.canShare({ files: input.files }));
  if (canShareFiles) {
    try { await nav!.share({ title: input.title, text: input.text, files: input.files }); return 'shared'; }
    catch (error) { if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'; }
  }
  let saved = 0;
  for (const file of input.files) {
    const result = await saveProductFile(platform, file.name, file).catch(() => ({ status: 'failed' as const }));
    if (result.status === 'saved' || result.status === 'requested') saved += 1;
  }
  if (input.text) { try { await nav?.clipboard?.writeText(input.text); if (!saved) return 'copied'; } catch { /* 저장만 된 경우도 있다 */ } }
  return saved ? 'saved' : input.files.length ? 'failed' : 'copied';
}

export function shareFilesMessage(result: ShareFilesResult, hasText = false): string {
  if (result === 'shared') return '공유 시트로 보냈어요.';
  if (result === 'saved') return `파일을 기기에 저장했어요.${hasText ? ' 글은 복사했어요.' : ''} 카톡에서 직접 첨부해 주세요.`;
  if (result === 'copied') return '글을 복사했어요. 카톡에 붙여 넣어 주세요.';
  if (result === 'cancelled') return '보내기를 취소했어요.';
  return '보내지 못했어요. 다시 시도해 주세요.';
}

export async function blobToFile(blob: Blob, name: string): Promise<File> {
  return new File([blob], name, { type: blob.type || 'application/octet-stream' });
}

export function extensionFor(mime: string): string {
  if (mime.includes('png')) return 'png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('heic')) return 'heic';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('quicktime')) return 'mov';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('pdf')) return 'pdf';
  return 'bin';
}
